/**
 * RTWO Native Currency Engine — Allocation Policy Service
 */

import type { AllocationId, CurrencyId, RTWOAllocationPolicy } from '../domain-models';
import type { AllocationPolicyResult, IAllocationPolicyService } from '../service-interfaces';
import type { NATVRepositories } from '../repositories/natv-repositories';
import type { LedgerAdapterRegistry } from '../registry/adapter-registries';
import { NATVInternalEventTypes } from '../events/natv-event-bus';
import { BaseNATVService, type NATVServiceContext } from './base-natv-service';

export class AllocationPolicyService extends BaseNATVService implements IAllocationPolicyService {
  readonly serviceId = 'natv-allocation-policy-001';
  readonly serviceName = 'Allocation Policy Service';
  readonly description =
    'Allocation policy service — placeholder, requires Genesis Configuration. No fake allocations, validator rewards, staking supply, or treasury balance.';
  readonly integrations = [
    'Core Engine',
    'Ledger Engine',
    'Staking/Rewards (future)',
    'Governance (future)',
  ];
  readonly requiresGenesisConfiguration = true;
  readonly requiresLedgerConnection = true;

  constructor(
    context: NATVServiceContext,
    private readonly repositories: NATVRepositories,
    private readonly ledgerRegistry: LedgerAdapterRegistry
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'waiting_for_genesis_configuration';
  }

  async configureAllocationPolicy(
    currencyId: CurrencyId,
    allocationType: string,
    allocationReference: string,
    vestingPolicyReference: string
  ): Promise<AllocationPolicyResult> {
    if (!this.ledgerRegistry.isConfigured()) {
      return {
        success: false,
        allocationId: null,
        error:
          'Allocation policy configuration requires Genesis Configuration — Genesis Configuration Not Created',
        isPlaceholder: true,
        source: 'Native Currency Engine — Genesis Configuration — Not Created',
      };
    }

    const adapterResult = await this.ledgerRegistry
      .getActiveAdapter()
      .configureAllocationPolicy(
        currencyId,
        allocationType,
        allocationReference,
        vestingPolicyReference
      );

    if (adapterResult?.policy) {
      this.repositories.allocationPolicies.save(adapterResult.policy);
      this.context.eventBus.emit(NATVInternalEventTypes.ALLOCATION_POLICY_CONFIGURED, {
        allocationId: adapterResult.policy.allocationId,
        currencyId,
      });
    }

    return {
      success: false,
      allocationId: null,
      error:
        'Allocation policy configuration requires Genesis Configuration — Genesis Configuration Not Created',
      isPlaceholder: true,
      source: 'Native Currency Engine — Genesis Configuration — Not Created',
    };
  }

  getAllAllocationPolicies(): RTWOAllocationPolicy[] {
    if (!this.ledgerRegistry.isConfigured()) return [];
    return this.repositories.allocationPolicies.findAll();
  }

  getAllocationPoliciesByCurrencyId(currencyId: CurrencyId): RTWOAllocationPolicy[] {
    if (!this.ledgerRegistry.isConfigured()) return [];
    return this.repositories.allocationPolicies.findByCurrencyId(currencyId);
  }

  getAllocationPolicy(allocationId: AllocationId): RTWOAllocationPolicy | null {
    if (!this.ledgerRegistry.isConfigured()) return null;
    return this.repositories.allocationPolicies.findById(allocationId);
  }
}
